import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { MutableRefObject, FC, useMemo } from 'react';
import CardHeader from '@mui/material/CardHeader';
import Card from '@mui/material/Card';
import { MemoizedBaseAvatar, Label } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { DEFAULT_CURRENCY } from '../../../../data';
import { AnimatedDivider } from '../../../../components';
import { TSingleTeam } from '../../../../types';
import { useTable } from '../../../../hooks/useTable';

type CommissionModel = 'split' | 'duplicate' | 'team steps';

type TTeamStat = {
  id: number;
  name: string;
  model: CommissionModel;
  netSales: number;
  commission: number;
  memberCount: number;
  totalOrders: number;
  members: TSingleTeam['team']['members']
};

interface BestSalesmanTableProps {
  teamStats: TTeamStat[];
}

const BestSalesmanTable: FC<BestSalesmanTableProps> = ({ teamStats }) => {
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const { t } = useTranslation(['dashboard', 'commissions', 'common', 'orders', 'inputs']);

  const { localText } = useTable(apiRef);

  const topSalespeople = useMemo(() => {
    const allMembers = teamStats.flatMap((team) => team.members.map((member) => ({
      ...member,
      teamName: team.name,
    })));

    return allMembers
      .filter((m) => m.netSales > 0)
      .sort((a, b) => b.netSales - a.netSales)
      .slice(0, 5)
      .map((member, index) => ({
        ...member,
        rank: `Top ${index + 1}`,
      }));
  }, [teamStats]);

  const productsColumns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('seller', { ns: 'dashboard' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedBaseAvatar avatarFile={null} avatarName={row.name} />
          <Typography variant="caption">{startCase(row.name)}</Typography>
        </Box>
      ),
    },
    {
      field: 'netSales',
      headerName: t('totalSales', { ns: 'commissions' }),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {`${row.netSales.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} ${DEFAULT_CURRENCY.mark}`}
        </Typography>
      ),
    },
    {
      field: 'teamName',
      headerName: t('team', { ns: 'inputs' }),
      minWidth: 150,
      renderCell: ({ row }) => (
        <Typography variant="caption">{row.teamName}</Typography>
      ),
    },
    {
      field: 'rank',
      headerName: t('rank', { ns: 'orders' }),
      minWidth: 100,
      renderCell: ({ row }) => (
        <Label
          variant="soft"
          color={
            (row.rank === 'Top 1' && 'primary')
            || (row.rank === 'Top 2' && 'info')
            || (row.rank === 'Top 3' && 'success')
            || (row.rank === 'Top 4' && 'warning')
            || 'error'
          }
        >
          {row.rank}
        </Label>
      ),
    },
  ];

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />;

  return (
    <Card sx={{ height: 492, pb: 6 }}>
      <CardHeader
        sx={{ p: 2 }}
        title={t('bestSalesman', { ns: 'orders' })}
      />
      <AnimatedDivider loading={false} />
      <DataGridPro
        apiRef={apiRef}
        rows={topSalespeople}
        columns={productsColumns}
        getRowId={(row) => row.email}
        hideFooter
        hideFooterPagination
        disableColumnMenu
        disableRowSelectionOnClick
        checkboxSelection={false}
        localeText={localText}
        slots={{
          noRowsOverlay: renderCustomNoRowsOverlay,
        }}
      />
    </Card>
  );
};

export { BestSalesmanTable };
